import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plug, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { listPlugins } from '@auw/api-client';
import { resolvePluginCatalog } from '@auw/plugins';
import { useTenant } from '@auw/tenant';
import { useChatStore, useUserStore, usePluginSelectionDraft, readPluginSelectionDraft, readDisabledMcpProviders } from '@auw/stores';
import { queryKeys, type Conversation, type WorkspaceMode } from '@auw/workspace-utils';
import type { DropdownMenuItem } from '../../common/DropdownMenu';

export function usePluginMenuItems({ open, enabled, conversation, mode }: {
  open: boolean;
  enabled: boolean;
  conversation: Conversation | null | undefined;
  mode: WorkspaceMode;
}): DropdownMenuItem[] {
  const { t } = useTranslation('common');
  const tenant = useTenant();
  const accountId = useUserStore(state => state.user?.userId ?? '');
  usePluginSelectionDraft(state => state.drafts);
  const query = useQuery({
    queryKey: [...queryKeys.plugins.list(), accountId],
    queryFn: async () => (await listPlugins()).plugins,
    enabled: open && enabled && !!accountId,
    staleTime: 30_000,
  });
  if (!enabled) return [];
  const disabled = conversation
    ? readDisabledMcpProviders(conversation.workspaceSettings)
    : readPluginSelectionDraft(accountId, mode);
  const catalog = resolvePluginCatalog({
    backendPlugins: query.data ?? [],
    hiddenPluginIds: tenant?.plugins?.hiddenIds,
    order: tenant?.plugins?.order,
    overrides: tenant?.plugins?.overrides,
  }).filter(entry => entry.backend);
  const toggle = (id: string) => {
    const current = conversation
      ? readDisabledMcpProviders(useChatStore.getState().conversations[conversation.id]?.workspaceSettings)
      : readPluginSelectionDraft(accountId, mode);
    const next = current.includes(id) ? current.filter(value => value !== id) : [...current, id];
    if (conversation) useChatStore.getState().setDisabledMcpProviders(conversation.id, next);
    else usePluginSelectionDraft.getState().setDraft(accountId, mode, next);
  };
  const children: DropdownMenuItem[] = query.isError ? [{
    id: 'plugins-retry', label: t('chat.pluginsRetry', { defaultValue: '목록 다시 불러오기' }),
    icon: <RefreshCw size={15} />, keepOpen: true, onClick: () => { void query.refetch(); },
  }] : query.isPending ? [{
    id: 'plugins-loading', label: t('chat.pluginsLoading', { defaultValue: '불러오는 중…' }), disabled: true, onClick: () => {},
  }] : catalog.length === 0 ? [{
    id: 'plugins-empty', label: t('chat.pluginsEmpty', { defaultValue: '사용 가능한 플러그인이 없습니다.' }), disabled: true, onClick: () => {},
  }] : catalog.map(entry => {
    const plugin = entry.backend!;
    const managed = ['tenant_identity', 'managed'].includes(entry.descriptor.ux?.connectionMode ?? '');
    const available = (managed || plugin.connected) && !plugin.error;
    return {
      id: `plugin:${plugin.id}`,
      label: entry.descriptor.label,
      icon: <Plug size={15} />,
      checked: available && !disabled.includes(plugin.id),
      disabled: !available,
      keepOpen: true,
      onClick: () => toggle(plugin.id),
    };
  });
  return [{
    id: 'plugin-connections', label: t('chat.pluginConnections', { defaultValue: '플러그인 연결' }),
    icon: <Plug size={15} />, onClick: () => {}, children,
  }];
}
